import React from 'react';
import { Link } from 'react-router-dom';
import { Text, Tag } from '@SBGSports/catapult-core';
import { TfActivity } from 'api';
import { DATE_FNS_SHORT_DATE_TIME_FORMAT } from '../../constants';
import { useDateFormatter } from '../../utils';
import styles from '../activity-side-menu.module.css';

interface BaseActivityListItemProps {
    activity: TfActivity;
    isSelected: boolean;
}

interface SingleModeItemProps extends BaseActivityListItemProps {
    mode: 'single';
    onSelect: (activityId: string) => void;
    getPath: (activityId: string) => string;
    isActive?: boolean;
}

interface MultiModeItemProps extends BaseActivityListItemProps {
    mode: 'multi';
    onSelect: (activity: TfActivity, index: number, isShiftClick: boolean, isMetaClick: boolean) => void;
    index: number;
    isActive?: boolean;
}

type ActivityListItemProps = SingleModeItemProps | MultiModeItemProps;

export const ActivityListItem: React.FC<ActivityListItemProps> = (props) => {
    const { activity, isSelected, mode } = props;
    const formatDate = useDateFormatter();
    const tag = activity.tags?.find((t) => t.tag_type_name === 'DayCode');
    const itemRef = React.useRef<HTMLElement>(null);
    const hasMounted = React.useRef(false);

    React.useEffect(() => {
        // item 0 starts with isActive=true but shouldn't steal focus on render
        if (!hasMounted.current) {
            hasMounted.current = true;
            return;
        }
        if (!props.isActive || !itemRef.current) {
            return;
        }
        // don't steal focus while the user is typing in a filter input
        const active = document.activeElement;
        const isTyping =
            active instanceof HTMLInputElement ||
            active instanceof HTMLTextAreaElement ||
            active instanceof HTMLSelectElement;
        if (!isTyping) {
            itemRef.current.focus();
        }
    }, [props.isActive]);

    const selectedClass = isSelected ? 'selected' : '';

    const content = (
        <div className={[styles['menu-activity'], styles[selectedClass]].join(' ')} data-test="activity-side-menu-card">
            <div className={styles['menu-activity-text']}>
                <Text variant="label-large" className="truncate">
                    {activity.name}
                </Text>
                <Text variant="body-small" className={styles['activity-menu-date']}>
                    {formatDate(activity.start_time, DATE_FNS_SHORT_DATE_TIME_FORMAT)}
                </Text>
            </div>
            <div className={styles['menu-activity-tag']}>
                {tag?.tag_name && <Tag label={tag.tag_name} size="sm" color="primary" />}
            </div>
        </div>
    );

    if (mode === 'single') {
        return (
            <Link
                key={activity.id}
                to={props.getPath(activity.id)}
                ref={itemRef as React.RefObject<HTMLAnchorElement>}
                className={styles['activity-list-item-wrapper']}
                tabIndex={props.isActive ? 0 : -1}
                aria-current={isSelected ? 'page' : undefined}
                onClick={(e) => {
                    props.onSelect(activity.id);
                    // detail === 0 means keyboard-triggered (Enter key) — keep focus in
                    // the list so the user can continue arrowing. Only move focus to main
                    // content on genuine mouse clicks (detail >= 1).
                    if (e.detail > 0) {
                        // calling .focus() synchronously would race with the browser's
                        // post-navigation handling, so rAF defers until React has flushed
                        // the route update and the browser has finished processing the event.
                        requestAnimationFrame(() => {
                            const main = document.getElementById('main-content') as HTMLElement | null;
                            if (main) {
                                if (!main.hasAttribute('tabindex')) {
                                    main.setAttribute('tabindex', '-1');
                                }
                                main.focus({ preventScroll: true });
                            }
                        });
                    }
                }}
            >
                {content}
            </Link>
        );
    } else {
        return (
            <div
                key={activity.id}
                role="option"
                ref={itemRef as React.RefObject<HTMLDivElement>}
                className={styles['activity-list-item-wrapper']}
                tabIndex={props.isActive ? 0 : -1}
                aria-selected={isSelected}
                onClick={(e) => {
                    if (e.shiftKey || e.metaKey || e.ctrlKey) {
                        e.preventDefault();
                    }
                    props.onSelect(activity, props.index, e.shiftKey, e.metaKey || e.ctrlKey);
                }}
                onMouseDown={(e) => {
                    if (e.shiftKey || e.metaKey || e.ctrlKey) {
                        e.preventDefault();
                    }
                }}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        e.preventDefault();
                        props.onSelect(activity, props.index, e.shiftKey, e.metaKey || e.ctrlKey);
                    }
                    if (e.key === ' ') {
                        e.preventDefault();
                        (e.currentTarget.closest('[role="listbox"]') as HTMLElement | null)?.scrollBy({ top: 80 });
                    }
                }}
                style={{ cursor: 'pointer', userSelect: 'none' }}
            >
                {content}
            </div>
        );
    }
};
